import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Star, Palette, Loader2, Check } from "lucide-react";

const PALETTE = ["#cfff04", "#FF3E9D", "#00D4FF", "#A855F7", "#FFD700", "#00ff41"];

export default function CustomizePanel({ coin, onUpdated }) {
  const [busy, setBusy] = useState(null); // sku being purchased | "accent"

  const buy = async (sku) => {
    setBusy(sku);
    try {
      const { data } = await api.post("/payments/checkout", {
        sku,
        coin_slug: coin.slug,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setBusy(null);
      toast.error(e.response?.data?.detail || "Checkout failed — try again");
    }
  };

  const setAccent = async (color) => {
    setBusy("accent");
    try {
      await api.patch(`/coins/${coin.slug}/accent`, { color });
      toast.success("Accent color updated");
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't set color");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="border border-[#3a3a3a] bg-[#121212] p-6 mb-6" data-testid="customize-panel">
      <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#cfff04] mb-4">
        // Customize your coin
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="border border-[#3a3a3a] bg-[#050505] p-4">
          <div className="flex items-center gap-2 font-display font-bold uppercase text-sm mb-1">
            <Star size={14} className="text-[#FFD700]" /> OG Badge
          </div>
          <p className="text-xs text-[#e2e2e2] mb-3">
            Permanent gold badge on your coin page and every /explore card.
          </p>
          {coin.og_badge ? (
            <span className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-[#FFD700]" data-testid="og-badge-active">
              <Check size={11} /> Active
            </span>
          ) : (
            <button
              onClick={() => buy("OG_BADGE")}
              disabled={busy === "OG_BADGE"}
              data-testid="buy-og-badge"
              className="btn-brutal bg-[#FFD700] text-[#050505] font-bold uppercase tracking-wider text-[11px] px-4 py-2 inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              {busy === "OG_BADGE" ? <Loader2 size={11} className="animate-spin" /> : <Star size={11} />}
              Get it — $5
            </button>
          )}
        </div>
        <div className="border border-[#3a3a3a] bg-[#050505] p-4">
          <div className="flex items-center gap-2 font-display font-bold uppercase text-sm mb-1">
            <Palette size={14} className="text-[#00D4FF]" /> Custom Theme
          </div>
          <p className="text-xs text-[#e2e2e2] mb-3">
            Unlock accent colors — make your coin page pop in your own style.
          </p>
          {coin.custom_theme_unlocked ? (
            <div className="flex items-center gap-2" data-testid="accent-swatches">
              {PALETTE.map((c) => (
                <button
                  key={c}
                  onClick={() => setAccent(c)}
                  disabled={busy === "accent"}
                  data-testid={`accent-${c.replace("#", "")}`}
                  aria-label={`Set accent ${c}`}
                  className={`w-7 h-7 border-2 transition-transform hover:scale-110 ${
                    (coin.accent_color || "") === c ? "border-white" : "border-[#333]"
                  }`}
                  style={{ background: c }}
                />
              ))}
            </div>
          ) : (
            <button
              onClick={() => buy("CUSTOM_THEME")}
              disabled={busy === "CUSTOM_THEME"}
              data-testid="buy-custom-theme"
              className="btn-brutal bg-[#00D4FF] text-[#050505] font-bold uppercase tracking-wider text-[11px] px-4 py-2 inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              {busy === "CUSTOM_THEME" ? <Loader2 size={11} className="animate-spin" /> : <Palette size={11} />}
              Unlock — $3
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
